import { t } from "@redotech/redo-i18n";
import PackageIcon from "@redotech/redo-icons/packages_filled.svg";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepType, StepTypeDetailsProps } from "../step";

/**
 * Editor-internal symbol for the Restocking step. Mirrors COMPENSATION_STEP's
 * approach: lives in the UI layer because the persisted shape is an
 * AdvancedFlow RestockingStep — this symbol only exists so the editor can
 * dispatch on it.
 *
 * Restocking is a terminal step: it has no downstream/next, matching
 * `DO_NOTHING`.
 */
export const RESTOCKING_STEP = Symbol("restocking_step");

/**
 * Per-step inventory policy. `restockDamaged` is always explicit: true =
 * restock damaged items reached by this step, false = skip them. (The
 * per-product `pendingProcessingInfo.shouldRestock` still wins at runtime.)
 */
export interface RestockingInventoryOverrides {
  restockDamaged: boolean;
}

export interface RestockingStepModel {
  type: typeof RESTOCKING_STEP;
  customTitle?: string;
  inventory?: RestockingInventoryOverrides;
}

export interface RestockingState {
  customTitle?: string;
  inventory?: RestockingInventoryOverrides;
}

export const RESTOCKING = (
  Details: React.ComponentType<StepTypeDetailsProps<RestockingState>>,
): StepType<RestockingState, RestockingStepModel> => ({
  Details: (props) => React.createElement(Details, props),
  get title() {
    return t("Restocking");
  },
  customTitle: (state) => state.customTitle,
  description: () => t("Restock returned items"),
  Icon: PackageIcon,
  iconColor: "gray",
  empty: { inventory: { restockDamaged: false } },
  downstream: () => [],
  maxDownstream: () => 0,
  layout: () => BlockLayout.FULL,
  fromModel(model) {
    return { customTitle: model.customTitle, inventory: model.inventory };
  },
  toModel(state) {
    return {
      type: RESTOCKING_STEP,
      customTitle: state.customTitle,
      inventory: state.inventory,
    };
  },
  stepDeleted(state) {
    return produce(state, () => {});
  },
  valid: () => true,
});
